// biome-ignore lint/suspicious/noShadowRestrictedNames: Ignore lint rule
import { CheckCircle, Close, Error, Info, Warning } from '@mui/icons-material'
import { IconButton, Snackbar, SnackbarContent } from '@mui/material'
import { SnackbarProps } from '@mui/material/Snackbar'
import classNames from 'classnames'
import React, { ReactElement } from 'react'
import type { AlertAction } from 'store/alert/alert'

import styles from './Alert.module.less'

export type AlertVariant = `success` | `error` | `warning` | `info`

const variantIcon = {
  success: CheckCircle,
  warning: Warning,
  error: Error,
  info: Info,
}

type AlertProps = {
  className?: string
  variant: AlertVariant
  message: string
  onClose?: () => void
  open: boolean
  SnackbarProps?: Partial<SnackbarProps>
  action?: AlertAction
  note?: string
  /**
   * Stable, semantic identifier for which alert fired — see `alertStore`. Deliberately **not**
   * named `key`: React consumes a `key` prop for reconciliation, so it would never reach the DOM.
   */
  alertKey?: string
}

function Alert({
  className,
  variant,
  message,
  onClose,
  open,
  SnackbarProps,
  alertKey,
  action,
  note,
  ...rest
}: AlertProps): ReactElement {
  const Icon = variantIcon[variant]

  let dynamicIcon

  if (variant === 'success') {
    dynamicIcon = styles.Alert_Success
  } else if (variant === 'error') {
    dynamicIcon = styles.Alert_Error
  } else if (variant === 'warning') {
    dynamicIcon = styles.Alert_Warning
  } else if (variant === 'info') {
    dynamicIcon = styles.Alert_Info
  }

  return message !== `` ? (
    <Snackbar
      anchorOrigin={{
        vertical: `top`,
        horizontal: `center`,
      }}
      open={open}
      autoHideDuration={30000}
      onClose={onClose}
      {...SnackbarProps}
    >
      <SnackbarContent
        data-testid="app-toast" // e2e: global toast/snackbar selector — do not remove
        data-alert-key={alertKey} // e2e: identifies which alert fired, independent of copy and locale — do not remove
        data-alert-variant={variant}
        className={classNames(dynamicIcon, className, styles.Alert_Wrapper)}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" className={styles.Alert_Message}>
            <Icon className={classNames(styles.Alert_Icon, styles.Alert_IconVariant)} />
            <span className={styles.Alert_Text}>
              <span>
                {message}
                {action && (
                  <button type="button" className={styles.Alert_Action} onClick={action.onClick}>
                    {action.label}
                  </button>
                )}
              </span>
              {note && <span className={styles.Alert_Note}>{note}</span>}
            </span>
          </span>
        }
        action={[
          <IconButton key="close" aria-label="Close" color="inherit" onClick={onClose} size="large">
            <Close className={styles.Alert_Icon} />
          </IconButton>,
        ]}
        {...rest}
      />
    </Snackbar>
  ) : (
    <></>
  )
}

export default Alert
